import { useCallback } from "react";

import { CardContent, CardContentProps, styled } from "@mui/material";

import { ExportFile, ExportV2 } from "@robin/common";

import FileDropzone from "./FileDropzone";
import Tabs from "./Tabs";
import { Patient } from "../models/patient";
import { useAppContext } from "../context/AppContext";
import { useCreatePatientData } from "../queries/patients-query";

const NAME = "PatientBody";
const patientBodyClasses = {
  dropzone: `${NAME}-Dropzone`,
  tabs: `${NAME}-Tabs`,
};

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  "&, &:last-child": { padding: theme.spacing(0) },
  display: "flex",
  flexDirection: "column",
  height: "100%",
  justifyContent: "flex-start",
  overflow: "hidden",
  position: "relative",
  [`& .${patientBodyClasses.dropzone}`]: {
    flex: "1 1 auto",
    margin: theme.spacing(2),
  },
  [`& .${patientBodyClasses.tabs}`]: {
    flex: "1 1 auto",
    height: "100%",
    overflow: "hidden",
  },
}));

export const PatientBody: React.FC<CardContentProps & { patient: Patient }> = ({
  patient,
}) => {
  const { dataConfig, setDataConfig } = useAppContext();

  const { mutateAsync: createPatientData } = useCreatePatientData();

  const executeImport = useCallback(
    (exportFile: ExportFile, data: ExportV2.ExportData) => {
      if (!patient) return;
      createPatientData({
        patientData: {
          data,
          exportDate: new Date(Date.parse(exportFile.exportDate)),
          deviceId: exportFile.deviceId,
          exportId: exportFile.exportId,
        },
        patient,
      })
        .then(() => {
          setDataConfig((s) => ({ ...s, exportId: exportFile.exportId }));
        })
        .catch((e: unknown) => {
          console.error("Data import error: ", e);
        });
    },
    [createPatientData, patient, setDataConfig]
  );

  return (
    <StyledCardContent>
      {dataConfig.exportId ? (
        <Tabs className={patientBodyClasses.tabs} patient={patient} />
      ) : (
        <FileDropzone
          className={patientBodyClasses.dropzone}
          onFileReceived={executeImport}
        />
      )}
    </StyledCardContent>
  );
};
